// Commission Report Export Modal
// Superadmin-only: select months to include in a downloadable PDF commission report
// Updated: Created for superadmin commission export feature

import React, { useState, useEffect } from 'react';
import { X, FileDown, Check, Calendar, DollarSign, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  generateCommissionPDF,
  calculateBookingCommission,
  getMonthLabel,
  MonthReportData,
  ReportSummary,
} from '../../utils/commissionReport';

interface CommissionReportModalProps {
  onClose: () => void;
}

interface AvailableMonth {
  monthKey: string;
  monthLabel: string;
  bookings: number;
  commission: number;
}

interface RawBooking {
  total_price: number;
  persons: number;
  status: string;
  booking_date: string;
  tours: {
    id: string;
    name: string;
    commission_type: 'percentage' | 'per_person' | null;
    commission_value: number | null;
  } | null;
}

const CommissionReportModal: React.FC<CommissionReportModalProps> = ({ onClose }) => {
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  const fetchAvailableMonths = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all non-cancelled bookings from Jan 2026 onward with tour commission info
      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select('total_price, persons, status, booking_date, tours (id, name, commission_type, commission_value)')
        .gte('booking_date', '2026-01-01')
        .neq('status', 'cancelled')
        .order('booking_date', { ascending: true });

      if (fetchError) throw fetchError;

      const bookings = (data || []) as RawBooking[];

      // Group by month
      const monthMap = new Map<string, AvailableMonth>();

      bookings.forEach((b) => {
        const monthKey = b.booking_date.slice(0, 7); // "2026-01"
        const commission = calculateBookingCommission(
          b.tours?.commission_type ?? null,
          b.tours?.commission_value ?? null,
          b.total_price || 0,
          b.persons || 0
        );

        const existing = monthMap.get(monthKey);
        if (existing) {
          existing.bookings += 1;
          existing.commission += commission;
        } else {
          monthMap.set(monthKey, {
            monthKey,
            monthLabel: getMonthLabel(monthKey),
            bookings: 1,
            commission,
          });
        }
      });

      // Sort descending (most recent first)
      const sorted = Array.from(monthMap.values()).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
      setAvailableMonths(sorted);
    } catch (err) {
      console.error('Error fetching available months:', err);
      setError('Failed to load available months. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMonth = (monthKey: string) => {
    setSelectedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedMonths(new Set(availableMonths.map((m) => m.monthKey)));
  };

  const deselectAll = () => {
    setSelectedMonths(new Set());
  };

  const selectedCount = selectedMonths.size;
  const estimatedCommission = availableMonths
    .filter((m) => selectedMonths.has(m.monthKey))
    .reduce((sum, m) => sum + m.commission, 0);

  const handleGenerate = async () => {
    if (selectedMonths.size === 0) return;

    setGenerating(true);
    setError(null);
    try {
      // Fetch all bookings for selected months
      const monthKeys = Array.from(selectedMonths).sort();
      const startDate = `${monthKeys[0]}-01`;
      const lastKey = monthKeys[monthKeys.length - 1];
      const [lastYear, lastMonth] = lastKey.split('-').map(Number);
      const endOfMonth = new Date(lastYear, lastMonth, 0, 23, 59, 59).toISOString();

      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select('total_price, persons, status, booking_date, tours ( id, name, commission_type, commission_value )')
        .gte('booking_date', startDate)
        .lte('booking_date', endOfMonth)
        .neq('status', 'cancelled')
        .order('booking_date', { ascending: true });

      if (fetchError) throw fetchError;

      const bookings = (data || []) as RawBooking[];

      // Group by month and tour
      const monthDataMap = new Map<string, MonthReportData>();
      const tourBreakdownMap = new Map<string, Map<string, {
        tourName: string;
        bookings: number;
        persons: number;
        revenue: number;
        commission: number;
        commissionType: 'percentage' | 'per_person' | null;
        commissionValue: number | null;
      }>>();

      bookings.forEach((b) => {
        const monthKey = b.booking_date.slice(0, 7);
        if (!selectedMonths.has(monthKey)) return;

        const commission = calculateBookingCommission(
          b.tours?.commission_type ?? null,
          b.tours?.commission_value ?? null,
          b.total_price || 0,
          b.persons || 0
        );

        // Month-level aggregation
        let monthData = monthDataMap.get(monthKey);
        if (!monthData) {
          monthData = {
            monthKey,
            monthLabel: getMonthLabel(monthKey),
            bookings: 0,
            totalRevenue: 0,
            totalCommission: 0,
            tourBreakdown: [],
          };
          monthDataMap.set(monthKey, monthData);
        }
        monthData.bookings += 1;
        monthData.totalRevenue += b.total_price || 0;
        monthData.totalCommission += commission;

        // Tour-level aggregation within month
        if (b.tours) {
          let tourMap = tourBreakdownMap.get(monthKey);
          if (!tourMap) {
            tourMap = new Map();
            tourBreakdownMap.set(monthKey, tourMap);
          }
          const tourKey = b.tours.id;
          let tourData = tourMap.get(tourKey);
          if (!tourData) {
            tourData = {
              tourName: b.tours.name,
              bookings: 0,
              persons: 0,
              revenue: 0,
              commission: 0,
              commissionType: b.tours.commission_type,
              commissionValue: b.tours.commission_value,
            };
            tourMap.set(tourKey, tourData);
          }
          tourData.bookings += 1;
          tourData.persons += b.persons || 0;
          tourData.revenue += b.total_price || 0;
          tourData.commission += commission;
        }
      });

      // Assemble final report
      const months = Array.from(monthDataMap.values()).map((m) => {
        const tourMap = tourBreakdownMap.get(m.monthKey);
        m.tourBreakdown = tourMap
          ? Array.from(tourMap.values()).sort((a, b) => b.commission - a.commission)
          : [];
        return m;
      }).sort((a, b) => a.monthKey.localeCompare(b.monthKey));

      const report: ReportSummary = {
        totalBookings: months.reduce((s, m) => s + m.bookings, 0),
        totalRevenue: months.reduce((s, m) => s + m.totalRevenue, 0),
        totalCommission: months.reduce((s, m) => s + m.totalCommission, 0),
        months,
      };

      generateCommissionPDF(report);
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1F2B1F] rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#953B0E] rounded-lg flex items-center justify-center">
              <FileDown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Export Commission Report</h2>
              <p className="text-sm text-white/40">Select months to include in the PDF report</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-[#E85C0D] animate-spin" />
              <span className="ml-3 text-white/60">Loading available months...</span>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-lg text-sm">
              {error}
            </div>
          ) : availableMonths.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No bookings found from January 2026 onward.</p>
            </div>
          ) : (
            <>
              {/* Selection controls */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-white/50">
                  {selectedCount} month{selectedCount === 1 ? '' : 's'} selected
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={selectAll}
                    className="text-xs px-3 py-1.5 bg-white/5 text-white/60 rounded-lg hover:bg-white/10 hover:text-white transition-all"
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAll}
                    className="text-xs px-3 py-1.5 bg-white/5 text-white/60 rounded-lg hover:bg-white/10 hover:text-white transition-all"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Month chips */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {availableMonths.map((month) => {
                  const isSelected = selectedMonths.has(month.monthKey);
                  return (
                    <button
                      key={month.monthKey}
                      onClick={() => toggleMonth(month.monthKey)}
                      className={`relative p-4 rounded-lg border text-left transition-all ${
                        isSelected
                          ? 'bg-[#E85C0D]/20 border-[#E85C0D] text-white'
                          : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-white/20'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-[#E85C0D] rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <p className="font-medium text-sm mb-1">{month.monthLabel}</p>
                      <div className="flex items-center gap-3 text-xs text-white/40">
                        <span>{month.bookings} booking{month.bookings === 1 ? '' : 's'}</span>
                        <span className="text-[#E85C0D]/80">${month.commission.toFixed(2)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer with summary and generate button */}
        {!loading && !error && availableMonths.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-4">
            {selectedCount > 0 && (
              <div className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#E85C0D]" />
                  <span className="text-sm text-white/60">Estimated total commission:</span>
                </div>
                <span className="text-lg font-bold text-[#E85C0D]">
                  ${estimatedCommission.toFixed(2)}
                </span>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-white/5 text-white/70 rounded-lg hover:bg-white/10 hover:text-white transition-all text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={selectedCount === 0 || generating}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-all text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4" />
                    Generate PDF
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionReportModal;
